
import React from 'react';
import { AuthGuard } from '@/components/AuthGuard';
import { Loader2, AlertCircle } from 'lucide-react';

// Import new section components
import {
  WorkspacePageHeader,
  WorkspaceStatsSection,
  WorkspaceListSection,
  WorkspaceDeleteDialog
} from '@/modules/app/components/workspace/sections';

// Import the new business logic hook
import { useWorkspacePageLogic } from '@/modules/app/hooks/useWorkspaceManagementLogic';

const AppWorkspacePage: React.FC = () => {
  // Use the centralized business logic hook
  const {
    // State
    searchFilter,
    selectedWorkspaces,
    isDeleteDialogOpen,
    workspaceToDelete,
    confirmationText,
    isDeleting,
    workspaceCounts,
    isLoadingCounts,
    filteredWorkspaces,
    totalSavedKeywords,
    totalSearches,
    totalAiAnalyses,
    defaultWorkspace,
    
    // From workspace list hook
    workspaces,
    isLoading,
    error,
    getErrorMessage,
    clearError,
    
    // From auth
    authLoading,
    isAuthenticated,
    userId,
    user,
    
    // Handlers
    handleRefresh,
    handleWorkspaceSelect,
    handleSelectAll,
    handleViewWorkspace,
    handleWorkspaceUpdate,
    handleWorkspaceDelete,
    handleWorkspaceCreated,
    handleDeleteClick,
    handleDeleteWorkspaceConfirm,
    handleDeleteCancel,
    handleSearchFilterChange,
    handleConfirmationTextChange,
  } = useWorkspacePageLogic();

  // Show loading state while authentication is happening
  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <AuthGuard>
        <div className="w-full min-h-screen">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Setting up your workspace...</span>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="w-full min-h-screen">
        {/* Header Section */}
        <WorkspacePageHeader
          user={user}
          defaultWorkspace={defaultWorkspace}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          onWorkspaceCreated={handleWorkspaceCreated}
        />

        {/* Error Display */}
        {error && (
          <div className="px-6 py-3">
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{getErrorMessage()}</span>
                <button 
                  onClick={clearError}
                  className="ml-auto h-6 w-6 p-0 text-destructive hover:text-destructive/80 bg-transparent border-none cursor-pointer"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Stats Cards Section */}
        <WorkspaceStatsSection
          totalSavedKeywords={totalSavedKeywords}
          totalSearches={totalSearches}
          totalAiAnalyses={totalAiAnalyses}
          isLoading={isLoading}
          isLoadingCounts={isLoadingCounts}
        />

        {/* Workspaces List Section */}
        <WorkspaceListSection
          filteredWorkspaces={filteredWorkspaces}
          isLoading={isLoading}
          isLoadingCounts={isLoadingCounts}
          searchFilter={searchFilter}
          onSearchFilterChange={handleSearchFilterChange}
          selectedWorkspaces={selectedWorkspaces}
          onWorkspaceSelect={handleWorkspaceSelect}
          onSelectAll={handleSelectAll}
          onViewWorkspace={handleViewWorkspace}
          onDeleteClick={handleDeleteClick}
          onWorkspaceUpdate={handleWorkspaceUpdate}
          onWorkspaceDelete={handleWorkspaceDelete}
          workspaceCounts={workspaceCounts}
          error={error}
        />

        {/* Delete Workspace Confirmation Dialog */}
        <WorkspaceDeleteDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={() => {}} // Controlled by handleDeleteCancel
          workspaceToDelete={workspaceToDelete}
          confirmationText={confirmationText}
          onConfirmationTextChange={handleConfirmationTextChange}
          isDeleting={isDeleting}
          onConfirmDelete={handleDeleteWorkspaceConfirm}
          onCancel={handleDeleteCancel}
        />
      </div>
    </AuthGuard>
  );
};

export default AppWorkspacePage;
